import {isClient} from 'hec/utils/common'
import {getTpaParamsFromUrl, getExpiryDateAndTimeForTpaCookies} from '../utils'
import {TpaBrandingCookieValues, TpaBrandingFromCustomerProfile, TpaGroupNames} from '../types'
import Cookies from 'js-cookie'
import capitalize from 'lodash/capitalize'

const TPA_BRANDING_COOKIE_NAME = 'tpaBranding'

/* ----------------------------------------
 * Format Helpers
 * ---------------------------------------- */

export const formatTpaGroupName = (groupName: string) =>
    groupName.toLowerCase() === TpaGroupNames.TPA ? groupName.toUpperCase() : capitalize(groupName)

export const formatTpaBrandingCookie = (values: TpaBrandingCookieValues): string => {
    const {afid, groupName, cid} = values
    return [afid, formatTpaGroupName(groupName), cid].join('|')
}

/* ----------------------------------------
 * Cookie Setters
 * ---------------------------------------- */

export const setTpaBrandingCookiesFromUrl = () => {
    if (!isClient) return

    const revTrackingValues = getTpaParamsFromUrl()
    if (!revTrackingValues) return

    const {groupName} = revTrackingValues
    const {expiryDate} = getExpiryDateAndTimeForTpaCookies(groupName)
    const newCookie = formatTpaBrandingCookie(revTrackingValues)
    const existingCookie = Cookies.get(TPA_BRANDING_COOKIE_NAME)

    if (newCookie && newCookie !== existingCookie) {
        Cookies.set(TPA_BRANDING_COOKIE_NAME, newCookie, {expires: expiryDate})
    }
}

export const setTpaBrandingCookiesFromCustomerData = (
    tpaValueFromCustomerData: TpaBrandingFromCustomerProfile
) => {
    if (!isClient) return

    const brandingValue = tpaValueFromCustomerData?.branding?.value
    if (!brandingValue) return

    const parts = brandingValue.split('|')
    const groupName = parts[1] || ''
    if (!groupName) return
    const {expiryDate: fallbackDate} = getExpiryDateAndTimeForTpaCookies(groupName)
    const expires = tpaValueFromCustomerData.branding?.expires_in || fallbackDate

    Cookies.set(TPA_BRANDING_COOKIE_NAME, brandingValue, {expires: new Date(expires)})
}

/* ----------------------------------------
 * Cookie Getters
 * ---------------------------------------- */

export const getTpaBrandingValuesFromCookies = (): TpaBrandingCookieValues | undefined => {
    if (!isClient) return

    const cookie = Cookies.get(TPA_BRANDING_COOKIE_NAME)
    if (!cookie) return

    const [afid, groupName, cid] = cookie.split('|')
    if (!afid || !groupName || !cid) return

    return {afid, groupName, cid}
}

/* ----------------------------------------
 * Customer Data Formatters
 * ---------------------------------------- */

export const formatTpaBrandingDataForCustomerData = ({
    tpaValueFromCustomerData,
    tpaBrandingCookie
}: {
    tpaValueFromCustomerData: TpaBrandingFromCustomerProfile
    tpaBrandingCookie: string
}): string => {
    const groupName = tpaBrandingCookie.split('|')[1] || ''
    const {expiryMs} = getExpiryDateAndTimeForTpaCookies(groupName)

    const newBrandingValue = {
        branding: {
            value: tpaBrandingCookie,
            expires_in: expiryMs
        }
    }

    return JSON.stringify({
        ...tpaValueFromCustomerData,
        ...newBrandingValue
    })
}

/* ----------------------------------------
 * Comparison Utilities
 * ---------------------------------------- */

export const determineShouldUpdateTpaBrandingCookie = (
    localCookie: string,
    customerData: TpaBrandingFromCustomerProfile
): boolean => {
    const serverCookie = customerData?.branding?.value
    return localCookie !== serverCookie
}
